import React, { useState, useEffect } from 'react';
import { View, ScrollView, TouchableOpacity, Alert, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, Shield, ShieldAlert, ShieldCheck } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import styles from './tracking.styles';
import settingsStyles from '../settings.styles';
import theme from '@/constants/colors';
import Constants from 'expo-constants';
import { checkBatteryOptimizationStatus, requestBatteryOptimizationExemption, openBatterySettings, BatteryCheckResult, OEMName } from '@/utils/batteryOptimization';
import { useTranslation } from 'react-i18next';

export default function TrackingSettingsScreen() {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { t } = useTranslation();
  const [batteryStatus, setBatteryStatus] = useState<BatteryCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    checkBattery();
  }, []);

  const checkBattery = async () => {
    setIsChecking(true);
    try {
      const result = await checkBatteryOptimizationStatus();
      setBatteryStatus(result);
    } catch (e) {
      console.warn('Failed to check battery optimization:', e);
    } finally {
      setIsChecking(false);
    }
  };

  const handleOptimize = async () => {
    try {
      if (batteryStatus?.isOptimized) {
        await requestBatteryOptimizationExemption();
        // Re-check after the user has had a chance to change settings
        setTimeout(checkBattery, 1000);
      } else if (batteryStatus?.needsOEMConfig) {
        await openBatterySettings();
        setTimeout(checkBattery, 2000);
      }
    } catch (e) {
      console.warn('Failed to open battery settings:', e);
    }
  };

  const getOEMDisplayName = (oem: OEMName): string => {
    switch (oem) {
      case 'samsung': return t('tracking.samsung');
      case 'xiaomi': return t('tracking.xiaomi');
      case 'huawei': return t('tracking.huawei');
      case 'oppo': return t('tracking.oppo');
      case 'vivo': return t('tracking.vivo');
      case 'oneplus': return t('tracking.oneplus');
      case 'asus': return t('tracking.asus');
      default: return t('tracking.yourDevice');
    }
  };

  const getStatusIcon = () => {
    if (!batteryStatus || Platform.OS !== 'android') {
      return <ShieldCheck size={48} color="#FFFE77" />;
    }
    if (batteryStatus.isOptimized || batteryStatus.needsOEMConfig) {
      return <ShieldAlert size={48} color="#F44336" />;
    }
    return <ShieldCheck size={48} color="#FFFE77" />;
  };

  const getStatusText = () => {
    if (Platform.OS !== 'android') {
      return t('tracking.notAvailablePlatform');
    }
    if (!batteryStatus) {
      return t('tracking.checkingStatus');
    }
    if (batteryStatus.isOptimized) {
      const oemPart = batteryStatus.oem !== 'none'
        ? ` ${getOEMDisplayName(batteryStatus.oem)}`
        : '';
      return batteryStatus.oem !== 'none'
        ? t('tracking.tailTrailsOptimized', { oem: getOEMDisplayName(batteryStatus.oem) })
        : t('tracking.tailTrailsOptimizedGeneric');
    }
    if (batteryStatus.needsOEMConfig) {
      return t('tracking.oemAdditionalSettings', { oem: getOEMDisplayName(batteryStatus.oem) });
    }
    return t('tracking.tailTrailsExempt');
  };

  const getStatusColor = () => {
    if (!batteryStatus || Platform.OS !== 'android') return theme.accentPrimary;
    if (batteryStatus.isOptimized || batteryStatus.needsOEMConfig) return '#F44336';
    return theme.accentPrimary;
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={settingsStyles.header}>
        <TouchableOpacity style={settingsStyles.backButton} onPress={() => router.back()}>
          <ArrowLeft size={20} color={theme.accentPrimary} />
        </TouchableOpacity>
        <Text style={settingsStyles.title}>{t('tracking.title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      {/* Build fingerprint — proves which binary this device runs: an install
          from before this change shows no version line at all. */}
      <Text style={[settingsStyles.title, { fontSize: 13, opacity: 0.6, alignSelf: 'center', marginBottom: 8 }]}
      >
        {`v${Constants.expoConfig?.version ?? '?'} · code ${Constants.expoConfig?.android?.versionCode ?? '?'}`}
      </Text>

      <ScrollView contentContainerStyle={styles.content}>
        {/* Status Card */}
        <View style={[styles.card, { borderColor: getStatusColor(), borderWidth: 2 }]}>
          <View style={styles.statusRow}>
            {getStatusIcon()}
            <View style={styles.statusText}>
              <Text style={[styles.statusTextTitle, { color: getStatusColor() }]}>
                {Platform.OS !== 'android' ? t('tracking.notAvailableLabel') :
                 !batteryStatus ? t('tracking.checking') :
                 batteryStatus.isOptimized ? t('tracking.trackingAtRiskTitle') :
                 batteryStatus.needsOEMConfig ? t('tracking.oemSettingsNeededTitle') :
                 t('tracking.trackingOptimizedTitle')}
              </Text>
              <Text style={styles.statusTextBody}>{getStatusText()}</Text>
            </View>
          </View>
        </View>

        {/* Optimize Button */}
        {Platform.OS === 'android' && batteryStatus && (batteryStatus.isOptimized || batteryStatus.needsOEMConfig) && (
          <TouchableOpacity style={styles.optimizeButton} onPress={handleOptimize}>
            <Text style={styles.optimizeButtonText}>
              {batteryStatus.isOptimized ? t('tracking.requestExemption') : t('tracking.openOemSettings')}
            </Text>
          </TouchableOpacity>
        )}

        {/* Samsung-specific guidance */}
        {Platform.OS === 'android' && batteryStatus?.oem === 'samsung' && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>{t('tracking.samsungSettings')}</Text>
            <Text style={styles.cardBody}>
              {t('tracking.samsungGuidance')}
            </Text>
            <View style={styles.stepsList}>
              <Text style={styles.stepText}>{t('tracking.samsungStep1')}</Text>
              <Text style={styles.stepText}>{t('tracking.samsungStep2')}</Text>
              <Text style={styles.stepText}>{t('tracking.samsungStep3')}</Text>
              <Text style={styles.stepText}>{t('tracking.samsungStep4')}</Text>
            </View>
          </View>
        )}

        {/* General guidance */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('tracking.tipsTitle')}</Text>
          <View style={styles.stepsList}>
            <Text style={styles.stepText}>{t('tracking.tip1')}</Text>
            <Text style={styles.stepText}>{t('tracking.tip2')}</Text>
            <Text style={styles.stepText}>{t('tracking.tip3')}</Text>
            <Text style={styles.stepText}>{t('tracking.tip4')}</Text>
            <Text style={styles.stepText}>{t('tracking.tip5')}</Text>
            <Text style={styles.stepText}>{t('tracking.tip6')}</Text>
          </View>
        </View>

        {/* Re-check button */}
        {Platform.OS === 'android' && (
          <TouchableOpacity style={styles.recheckButton} onPress={checkBattery}>
            <Text style={styles.recheckButtonText}>{t('tracking.recheckStatus')}</Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );
}